import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Brain, Loader2 } from "lucide-react";
import { Math } from "@/components/Math";

export function SuperCalcDemo() {
  const [stage, setStage] = useState<"idle" | "typing" | "solving" | "complete">("idle");
  const [typedText, setTypedText] = useState("");
  const fullQuestion = "A ball is thrown at 20 m/s at a 45° angle. How far does it travel?";
  
  useEffect(() => {
    if (stage === "idle") {
      const timeout = setTimeout(() => setStage("typing"), 1000);
      return () => clearTimeout(timeout);
    }

    if (stage === "typing") {
      if (typedText.length < fullQuestion.length) {
        const timeout = setTimeout(() => {
          setTypedText(fullQuestion.slice(0, typedText.length + 1));
        }, 50);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => setStage("solving"), 500);
        return () => clearTimeout(timeout);
      }
    }

    if (stage === "solving") {
      const timeout = setTimeout(() => setStage("complete"), 1500);
      return () => clearTimeout(timeout);
    }

    if (stage === "complete") {
      const timeout = setTimeout(() => {
        setStage("idle");
        setTypedText("");
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [stage, typedText, fullQuestion]);

  return (
    <div className="w-full bg-background/80 backdrop-blur rounded-lg border overflow-hidden">
      {/* Input Section */}
      <div className="p-3 sm:p-4 border-b bg-muted/30 overflow-hidden">
        <p className="text-xs text-muted-foreground mb-2">You ask:</p>
        <div className="bg-background p-2 sm:p-3 rounded min-h-[60px] font-mono text-xs sm:text-sm break-words overflow-hidden">
          {typedText}
          {stage === "typing" && (
            <span className="inline-block w-0.5 h-4 bg-primary ml-0.5 animate-pulse" />
          )}
        </div>
        <div className="mt-3 flex justify-end">
          <Button 
            size="sm" 
            className="gap-2"
            disabled={stage === "typing" || typedText.length === 0}
          >
            {stage === "solving" ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Solving...
              </>
            ) : (
              <>
                <Brain className="h-3 w-3" />
                Solve
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Answer Section */}
      <div className="p-3 sm:p-4 min-h-[300px] sm:min-h-[400px] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between mb-2 gap-2">
          <p className="text-xs text-muted-foreground">SuperCalc AI answers:</p>
          <Button 
            size="sm" 
            variant="outline"
            className="text-xs h-7 gap-1.5 border-primary/20 hover:bg-primary/10 shrink-0"
            asChild
          >
            <a href="/membership">
              <Brain className="h-3 w-3" />
              <span className="hidden sm:inline">Get Full Access</span>
              <span className="sm:hidden">Access</span>
            </a>
          </Button>
        </div>
        <div 
          className={`transition-all duration-500 flex-1 ${
            stage === "complete" 
              ? "opacity-100 translate-y-0" 
              : "opacity-0 translate-y-2"
          }`}
        >
          {stage === "complete" && (
            <div className="space-y-3 text-xs sm:text-sm w-full overflow-hidden">
              <p className="font-semibold">Solution:</p>
              <p className="break-words">For projectile motion with initial velocity v₀ = 20 m/s and angle θ = 45°:</p>
              
              <div className="bg-muted/50 p-2 sm:p-3 rounded w-full overflow-hidden">
                <p className="text-xs text-muted-foreground mb-2">Range formula:</p>
                <div className="w-full overflow-x-auto">
                  <Math block className="text-[10px] sm:text-sm">
                    {String.raw`R = \frac{v_0^2 \sin(2\theta)}{g}`}
                  </Math>
                </div>
              </div>

              <div className="bg-muted/50 p-2 sm:p-3 rounded w-full overflow-hidden">
                <p className="text-xs text-muted-foreground mb-2">Calculation:</p>
                <div className="w-full overflow-x-auto">
                  <Math block className="text-[10px] sm:text-sm">
                    {String.raw`R = \frac{(20)^2 \times \sin(90°)}{9.81} = 40.8 \text{ meters}`}
                  </Math>
                </div>
              </div>

              <p className="text-primary font-medium break-words">
                ✓ The ball travels approximately <strong>40.8 meters</strong>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
